import React, { useState, useEffect } from 'react';
import { DelegationType } from '../types.js';
import { CloseIcon } from './icons';
import ContactSelector from './ContactSelector';

// Mock contacts data - in a real app, this would come from a service
const mockContacts  = [
  { id: '1', name: 'Shiva', email: 's@busin.us', role: 'Project Manager' },
  { id: '2', name: 'Dharma Teja', email: 'dharma@busin.us', role: 'Legal Counsel' },
  { id: '3', name: 'Arvind Patil', email: 'arvindpatil@busin.us', role: 'Financial Advisor' },
  { id: '4', name: 'Sarah Chen', email: 'sarah@busin.us', role: 'Marketing Director' },
  { id: '5', name: 'David Brown', email: 'david@busin.us', role: 'Tech Lead' },
  { id: '6', name: 'Emma Davis', email: 'emma@busin.us', role: 'HR Manager' },
  { id: '7', name: 'Mohamed Ali', email: 'mohamed@busin.us', role: 'Operations Manager' },
  { id: '8', name: 'Olivia Smith', email: 'olivia@busin.us', role: 'Sales Director' },
];

const DelegationPanel = ({ 
  node, 
  onClose, 
  onUpdate, 
  isMobile,
  isDelegationOpen,
  setIsDelegationOpen 
}) => {
  // For mobile, we render a modal-like delegation panel
  if (isMobile) {
    if (!isDelegationOpen) {
      return null;
    }
    
    return (
      <>
        {/* Mobile delegation overlay */}
        <div 
          className="fixed inset-0 bg-black/50 z-40 sm:hidden"
          onClick={() => setIsDelegationOpen(false)}
        />
        
        {/* Mobile delegation panel */}
        <aside 
          className="fixed top-0 right-0 h-full bg-dark-surface border-l border-dark-border p-6 z-50 flex flex-col transition-transform duration-300 ease-in-out sm:hidden"
          style={{ width: '90%', maxWidth: '320px' }}
        >
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-dark-text-primary">Delegation Settings</h2>
            <button 
              onClick={() => setIsDelegationOpen(false)}
              className="p-2 rounded-md text-dark-text-secondary hover:bg-dark-bg"
            >
              <CloseIcon className="w-5 h-5" />
            </button>
          </div>
          
          {renderDelegationContent()}
        </aside>
      </>
    );
  }

  // Desktop delegation panel
  function renderDelegationContent() {
    // Only show the panel when a node is selected and delegation is needed
    if (!node) {
      return null;
    }

    const delegation = node.data.delegation || {
      delegationType: DelegationType.NONE,
      delegateToHuman: {
        contactId: '',
        contactName: '',
        instructions: '',
        expectedResponseTime: 0
      },
      delegateToAgent: {
        agentId: '',
        agentName: '',
        configuration: {}
      }
    };

    const [delegationType, setDelegationType] = useState(delegation.delegationType);
    const [humanDelegation, setHumanDelegation] = useState(delegation.delegateToHuman);
    const [agentDelegation, setAgentDelegation] = useState(delegation.delegateToAgent);
    // Add state for business plan specific fields
    const [assignedTo, setAssignedTo] = useState(node.data.assignedTo || '');
    const [priority, setPriority] = useState(node.data.priority || 'MEDIUM');
    const [estimatedDuration, setEstimatedDuration] = useState(node.data.estimatedDuration || '');

    const handleSave = () => {
      const updatedDelegation = {
        delegationType,
        delegateToHuman: delegationType === DelegationType.HUMAN ? humanDelegation : delegation.delegateToHuman,
        delegateToAgent: delegationType === DelegationType.AGENT ? agentDelegation : delegation.delegateToAgent
      };
      
      onUpdate(node.id, { 
        data: { 
          ...node.data, 
          delegation: updatedDelegation,
          assignedTo,
          priority,
          estimatedDuration
        } 
      });
      onClose();
    };

    const handleCancel = () => {
      // Reset to original values
      setDelegationType(delegation.delegationType);
      setHumanDelegation(delegation.delegateToHuman);
      setAgentDelegation(delegation.delegateToAgent);
      setAssignedTo(node.data.assignedTo || '');
      setPriority(node.data.priority || 'MEDIUM');
      setEstimatedDuration(node.data.estimatedDuration || '');
      onClose();
    };

    const handleContactSelect = (contact) => {
      setHumanDelegation({
        ...humanDelegation,
        contactId: contact.id,
        contactName: contact.name
      });
    };

    return (
      <>
        <div className="flex items-center gap-3 mb-6">
          <h3 className="text-lg font-bold text-dark-text-primary">Delegate: {node.label}</h3>
        </div>

        <div className="flex-grow overflow-y-auto space-y-4 -mr-2 pr-2">
          {/* Business Plan Fields */}
          <div>
            <label className="block text-sm font-medium text-dark-text-secondary mb-2">Assigned To</label>
            <input
              type="text"
              value={assignedTo}
              onChange={(e) => setAssignedTo(e.target.value)}
              placeholder="e.g., Founder, Legal Team, Marketing Manager"
              className="w-full bg-dark-bg border border-dark-border rounded-md p-2 text-sm text-dark-text-primary focus:ring-1 focus:ring-brand-primary focus:outline-none transition"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-dark-text-secondary mb-2">Priority</label>
            <select
              value={priority}
              onChange={(e) => setPriority(e.target.value)}
              className="w-full bg-dark-bg border border-dark-border rounded-md p-2 text-sm text-dark-text-primary focus:ring-1 focus:ring-brand-primary focus:outline-none transition"
            >
              <option value="CRITICAL">Critical</option>
              <option value="HIGH">High</option>
              <option value="MEDIUM">Medium</option>
              <option value="LOW">Low</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-dark-text-secondary mb-2">Estimated Duration</label>
            <input
              type="text"
              value={estimatedDuration}
              onChange={(e) => setEstimatedDuration(e.target.value)}
              placeholder="e.g., 2 weeks, 3 days, 1 month"
              className="w-full bg-dark-bg border border-dark-border rounded-md p-2 text-sm text-dark-text-primary focus:ring-1 focus:ring-brand-primary focus:outline-none transition"
            />
          </div>

          {/* Delegation Type Selection */}
          <div>
            <label className="block text-sm font-medium text-dark-text-secondary mb-2">Delegation Type</label>
            <div className="space-y-2">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="delegationType"
                  value={DelegationType.NONE}
                  checked={delegationType === DelegationType.NONE}
                  onChange={(e) => setDelegationType(e.target.value)}
                  className="mr-2"
                />
                <span className="text-dark-text-primary">No Delegation</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="delegationType"
                  value={DelegationType.HUMAN}
                  checked={delegationType === DelegationType.HUMAN}
                  onChange={(e) => setDelegationType(e.target.value)}
                  className="mr-2"
                />
                <span className="text-dark-text-primary">Delegate to Human</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="delegationType"
                  value={DelegationType.AGENT}
                  checked={delegationType === DelegationType.AGENT}
                  onChange={(e) => setDelegationType(e.target.value)}
                  className="mr-2"
                />
                <span className="text-dark-text-primary">Delegate to Agent</span>
              </label>
            </div>
          </div>

          {/* Human Delegation Form - Only show when HUMAN is selected */}
          {delegationType === DelegationType.HUMAN && (
            <div className="space-y-4">
              <h4 className="font-medium text-dark-text-primary">Human Delegation</h4>
              
              <div>
                <label className="block text-sm font-medium text-dark-text-secondary mb-1">Contact</label>
                <ContactSelector
                  contacts={mockContacts}
                  selectedContactId={humanDelegation.contactId}
                  onSelectContact={handleContactSelect}
                  placeholder="Select a contact"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-dark-text-secondary mb-1">Instructions (Optional)</label>
                <textarea
                  value={humanDelegation.instructions}
                  onChange={(e) => setHumanDelegation({...humanDelegation, instructions: e.target.value})}
                  placeholder="Task instructions"
                  rows={3}
                  className="w-full bg-dark-bg border border-dark-border rounded-md p-2 text-sm text-dark-text-primary focus:ring-1 focus:ring-brand-primary focus:outline-none transition"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-dark-text-secondary mb-1">Expected Response Time (hours)</label>
                <input
                  type="number"
                  value={humanDelegation.expectedResponseTime}
                  onChange={(e) => setHumanDelegation({...humanDelegation, expectedResponseTime: parseInt(e.target.value) || 0})}
                  className="w-full bg-dark-bg border border-dark-border rounded-md p-2 text-sm text-dark-text-primary focus:ring-1 focus:ring-brand-primary focus:outline-none transition"
                />
              </div>
            </div>
          )}

          {/* Agent Delegation Form - Only show when AGENT is selected */}
          {delegationType === DelegationType.AGENT && (
            <div className="space-y-4">
              <h4 className="font-medium text-dark-text-primary">Agent Delegation</h4>
              
              <div>
                <label className="block text-sm font-medium text-dark-text-secondary mb-1">Agent</label>
                <select
                  value={agentDelegation.agentName}
                  onChange={(e) => setAgentDelegation({...agentDelegation, agentName: e.target.value})}
                  className="w-full bg-dark-bg border border-dark-border rounded-md p-2 text-sm text-dark-text-primary focus:ring-1 focus:ring-brand-primary focus:outline-none transition"
                >
                  <option value="">Select an agent</option>
                  <option value="Research Agent">Research Agent</option>
                  <option value="Writing Agent">Writing Agent</option>
                  <option value="Analysis Agent">Analysis Agent</option>
                  <option value="Code Generation Agent">Code Generation Agent</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-dark-text-secondary mb-1">Configuration</label>
                <textarea
                  value={JSON.stringify(agentDelegation.configuration, null, 2)}
                  onChange={(e) => {
                    try {
                      const config = JSON.parse(e.target.value);
                      setAgentDelegation({...agentDelegation, configuration: config});
                    } catch (err) {
                      // Invalid JSON, keep as is
                    }
                  }}
                  placeholder='{"param1": "value1", "param2": "value2"}'
                  rows={4}
                  className="w-full bg-dark-bg border border-dark-border rounded-md p-2 text-sm text-dark-text-primary focus:ring-1 focus:ring-brand-primary focus:outline-none transition font-mono"
                />
              </div>
            </div>
          )}
        </div>

        <div className="flex-shrink-0 pt-4 mt-4 border-t border-dark-border space-x-2">
          <button
            onClick={handleSave}
            className="px-4 py-2 bg-brand-primary text-white rounded-md hover:bg-brand-primary/90 transition-colors"
          >
            Save
          </button>
          <button
            onClick={handleCancel}
            className="px-4 py-2 bg-dark-bg border border-dark-border text-dark-text-primary rounded-md hover:bg-dark-border transition-colors"
          >
            Cancel
          </button>
        </div>
      </>
    );
  }

  // Only show the panel when a node is selected and delegation is needed
  if (!node) {
    return null;
  }

  return (
    <aside className="w-80 bg-dark-surface border-l border-dark-border flex-shrink-0 p-6 z-20 flex flex-col hidden sm:flex">
      {renderDelegationContent()}
    </aside>
  );
};

export default DelegationPanel;